import { log } from './log';

/**
 * Desktop notifications, for the one thing a counter must not miss.
 *
 * There is exactly one trigger: **a queued bill the server has refused.** That
 * bill is in a customer's hands and now exists nowhere else, and it happens
 * during a sync that runs in the background — very often while the tab is
 * behind a WhatsApp window or the shop's own billing screen. An in-app toast
 * for that is a toast nobody sees.
 *
 * Nothing else notifies. A permission that fires for routine events gets turned
 * off, and then the one message that mattered is off with it.
 */

export type NotifyPermission = 'granted' | 'denied' | 'default' | 'unsupported';

export function notifyPermission(): NotifyPermission {
  if (!('Notification' in globalThis)) return 'unsupported';
  return Notification.permission as NotifyPermission;
}

/** Asks once. A browser that has already denied is not asked again — it cannot be. */
export async function requestNotifyPermission(): Promise<NotifyPermission> {
  if (!('Notification' in globalThis)) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission as NotifyPermission;
  try {
    return (await Notification.requestPermission()) as NotifyPermission;
  } catch {
    return 'denied';
  }
}

/**
 * Shows a notification if allowed, and does nothing at all if not.
 *
 * Never a bill's contents — a notification renders on a lock screen, which is
 * the last place a customer's name and medicines should appear. The bill number
 * is enough to find it.
 */
export function notify(title: string, body: string): void {
  if (notifyPermission() !== 'granted') return;
  try {
    new Notification(title, { body, tag: 'tlp-counter', icon: '/logomark.png' });
  } catch (error) {
    // Some browsers only permit notifications from a service worker context.
    log.warn('sync', 'could not show a notification', error);
  }
}
